import { Injectable } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import { doc, Firestore, getDoc } from '@angular/fire/firestore';
import { from, Observable } from 'rxjs';
import { UserService } from '../../../../../chat-common/src/lib/services/user.service';
import { BcraReport, BreastDensity } from './bcra-report.model';

@Injectable({ providedIn: 'root' })
export class BcraReportService {
  constructor(
    private fs: Firestore,
    private auth: Auth,
    private userService: UserService,
  ) {}

  getReport(): Observable<BcraReport> {
    return from(this.loadReport());
  }

  private async loadReport(): Promise<BcraReport> {
    // On a fresh page load Firebase Auth restores the session asynchronously
    await this.auth.authStateReady();

    const uid = this.userService.user?.id;
    if (!uid) {
      throw new Error('No user id available to load the BCRA report');
    }
    const quizPath = ['v0', 'data', 'user', uid, 'quiz', 'quiz-bcrisk'] as const;
    const snap = await getDoc(doc(this.fs, ...quizPath, 'results-gail', 'current'));
    if (!snap.exists()) {
      // No results yet: the survey is either in progress or not started
      const progress = await getDoc(doc(this.fs, ...quizPath, 'temp-progress', 'current'));
      return { status: progress.exists() ? 'incomplete' : 'not-started', result: null };
    }

    const data = snap.data();
    const response = parseJson(data['responseData']);
    const summary = response?.summary?.[0];
    const lifetimeRisk = parseFloat(summary?.AbsRisk);
    const averageRisk = parseFloat(summary?.AbsRisk_Avg);
    if (data['verified'] !== true || response?.errors?.length || isNaN(lifetimeRisk) || isNaN(averageRisk)) {
      // Answers are submitted but the result isn't verified or calculated yet
      return { status: 'pending', result: null };
    }

    const request = parseJson(data['requestData']);
    return {
      status: 'ready',
      result: {
        lifetimeRisk,
        averageRisk,
        breastDensity: toBreastDensity(request?.personalHealthHistory?.breastDensity),
        answeredAt: toDate(request?.createdAt),
      },
    };
  }
}

/** Firestore may hold the payloads as JSON strings or as maps. */
function parseJson(value: unknown): any {
  if (typeof value !== 'string') return value ?? null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function toDate(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date;
}

/** BI-RADS categories: a/b are not dense, c/d are dense. */
function toBreastDensity(category: unknown): BreastDensity {
  switch (typeof category === 'string' ? category.toLowerCase() : null) {
    case 'a':
    case 'b':
      return 'Not Dense';
    case 'c':
    case 'd':
      return 'Dense';
    default:
      return 'Unknown';
  }
}
